import React from "react";
import { IShop } from "interfaces";
import cls from "./shopList.module.scss";
import { Grid, Skeleton, useMediaQuery } from "@mui/material";
import ShopCard from "components/shopCard/shopCard";
import { useTranslation } from "react-i18next";
import LoadMore from "components/button/load-more";
import Empty from "components/empty/empty";
import { normalizeShopInitial, SHOP_INITIALS, shopAlphabetCopy, ShopInitial } from "utils/shopAlphabet";

type Props = {
  title?: string;
  shops: IShop[];
  loading?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  fetchingNextPage?: boolean;
  compact?: boolean;
  initial?: ShopInitial;
  onInitialChange?: (initial: ShopInitial) => void;
  error?: boolean;
  onRetry?: () => void;
};

export default function ShopList({
  title,
  shops,
  loading,
  fetchNextPage,
  hasNextPage = false,
  fetchingNextPage = false,
  compact = false,
  initial = "",
  onInitialChange,
  error = false,
  onRetry,
}: Props) {
  const { t, i18n } = useTranslation();
  const copy = shopAlphabetCopy(i18n.language);
  const showAlphabet = !!onInitialChange;
  const selectedInitial = normalizeShopInitial(initial);
  const isDesktop = useMediaQuery("(min-width:1140px)");
  const handleLoadNexPage = () => {
    if (hasNextPage && fetchNextPage) {
      fetchNextPage();
    }
  };

  if (!showAlphabet && !loading && !shops?.length) {
    return <Empty text={t("no.shops.found")} />;
  }

  return (
    <section
      className={`${cls.container} ${compact ? cls.compact : ""}`}
      aria-busy={loading || fetchingNextPage}
      style={{
        display: !showAlphabet && !loading && shops.length === 0 ? "none" : "block",
      }}
    >
      <div className={cls.wrapper}>
        <div className={cls.header}>
          <h2 className={cls.title}>{title}</h2>
        </div>
        {showAlphabet && (
          <div className={cls.alphabet} role="group" aria-label={t("manufacturers.initial.label", { defaultValue: copy.label })}>
            {SHOP_INITIALS.map((letter) => (
              <button
                key={letter || "all"}
                type="button"
                className={cls.initial}
                aria-pressed={selectedInitial === letter}
                onClick={() => onInitialChange?.(letter)}
              >
                {letter || t("manufacturers.initial.all", { defaultValue: copy.all })}
              </button>
            ))}
          </div>
        )}
        {showAlphabet && error && (
          <div className={cls.feedback} role="alert">
            <p>{t("manufacturers.initial.error", { defaultValue: copy.error })}</p>
            {onRetry && <button type="button" onClick={onRetry}>{t("manufacturers.initial.retry", { defaultValue: copy.retry })}</button>}
          </div>
        )}
        {showAlphabet && !loading && !error && !shops.length && (
          <p className={cls.empty} role="status">{t("manufacturers.initial.empty", { defaultValue: copy.empty })}</p>
        )}
        <Grid container spacing={compact ? 2 : isDesktop ? 4 : 2}>
          {!loading
            ? shops.map((item) => (
                <Grid key={item.id} item xs={12} sm={6} lg={3}>
                  <ShopCard data={item} compact={compact} />
                </Grid>
              ))
            : Array.from(new Array(4)).map((_, idx) => (
                <Grid key={"shops" + idx} item xs={12} sm={6} lg={3}>
                  <Skeleton variant="rectangular" className={cls.shimmer} />
                </Grid>
              ))}
        </Grid>
        {hasNextPage && (
          <div className={cls.loadMore}>
            <LoadMore loading={fetchingNextPage} onClick={handleLoadNexPage}>
              {t("load.more")}
            </LoadMore>
          </div>
        )}
      </div>
    </section>
  );
}
